
import React, { useState, useEffect } from "react";
import { Guard } from "../types";
import { GuardInfo } from "./GuardInfo";
import { TeamManagement } from "./TeamManagement";
import { Separator } from "@/components/ui/separator";
import { useUserRoles } from "@/hooks/useUserRoles";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GuardEquipmentDetails } from "./GuardEquipmentDetails";
import { ImprovedDeleteGuardDialog } from "./ImprovedDeleteGuardDialog";
import { ArchiveGuardDialog } from "./ArchiveGuardDialog";
import { supabase } from "@/integrations/supabase/client";

interface GuardExpandedInfoProps {
  guard: Guard;
  onGuardRemoved?: () => void;
  onGuardUpdated?: (updatedGuard: Guard) => void;
}

export const GuardExpandedInfo: React.FC<GuardExpandedInfoProps> = ({
  guard,
  onGuardRemoved,
  onGuardUpdated,
}) => {
  const { isAdmin } = useUserRoles();
  const [currentGuard, setCurrentGuard] = useState<Guard>(guard);
  const [activeTab, setActiveTab] = useState("info");
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    setCurrentGuard(guard);
  }, [guard]);

  // Check if the guard has super_admin role
  useEffect(() => {
    const checkSuperAdminRole = async () => {
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', guard.id)
        .eq('role', 'super_admin')
        .maybeSingle();
      
      setIsSuperAdmin(!!data);
    };

    checkSuperAdminRole();
  }, [guard.id]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  const handleGuardUpdate = (updatedGuard: Guard) => {
    setCurrentGuard(updatedGuard);
    if (onGuardUpdated) {
      onGuardUpdated(updatedGuard);
    }
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="info" className="w-full" onValueChange={handleTabChange}>
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="info">General Info</TabsTrigger>
          <TabsTrigger value="teams">Teams</TabsTrigger>
          <TabsTrigger value="equipment">Equipment</TabsTrigger>
        </TabsList>

        <TabsContent value="info">
          <GuardInfo guard={currentGuard} onUpdate={handleGuardUpdate} />
          {isAdmin && !isSuperAdmin && (
            <>
              <Separator className="my-4" />
              <div className="flex justify-end space-x-2">
                <ArchiveGuardDialog
                  guard={currentGuard}
                  onGuardArchived={onGuardRemoved}
                />
                <ImprovedDeleteGuardDialog
                  guard={currentGuard}
                  onGuardRemoved={onGuardRemoved}
                />
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="teams">
          <TeamManagement guard={currentGuard} />
        </TabsContent>
        
        <TabsContent value="equipment">
          <GuardEquipmentDetails guardId={currentGuard.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
